/**
 * Outer Type tabs (MAS / NC / Packout MAS / Packout NC) and inner
 * NDC tabs, both rendered with PrimeReact TabView + NDCHeader so the
 * v2 screen matches the FDPUpdated mock pixel-by-pixel.
 *
 * Structure (mirrors mock):
 *   <TabView> outer Type tabs (NDCHeader headers)
 *     <TabView> inner NDC tabs (NDCHeader headers)
 *       PackSizeChips
 *       MasForecastGrid    (15-month editable matrix + TOTAL row)
 *       SUPricesTable      (derived monthly SU per facility + TOTAL)
 *       SaveBar
 *
 * Empty Types are dropped from the outer tab list (per locked-in rule
 * "hide empty tabs"). Inner NDC tabs are always rendered, even with a
 * single NDC, so the operator always sees what they're editing.
 */

import { observer } from 'mobx-react';
import { TabPanel, TabView } from 'primereact/tabview';

import NDCHeader from 'screens/FDPScreen/components/UnconstraintForecastScreen/components/NDCHeader';

import { OUTER_TAB_LABELS } from './editStore';
import { useEditStore } from './editStoreContext';
import MasForecastGrid from './MasForecastGrid';
import NCForecastGrid from './NCForecastGrid';
import PackoutPackRow from './PackoutPackRow';
import PackSizeChips from './PackSizeChips';
import SaveBar from './SaveBar';
import SUPricesTable from './SUPricesTable';
import VarianceFlagPill from './VarianceFlagPill';
import styles from './forecastTabs.module.scss';

const ForecastTabs = observer(() => {
  const store = useEditStore();
  const tabs = store.outerTabs;

  if (tabs.length === 0) {
    return (
      <div className={styles.empty}>
        No forecast data available for the selected NDCs.
      </div>
    );
  }

  const activeOuterIndex = Math.max(
    0,
    tabs.findIndex(t => t.type === store.activeOuterTab)
  );
  const activeOuter = tabs[activeOuterIndex] ?? tabs[0];
  const activeInnerNDC =
    store.activeInnerNDCByTab[activeOuter.type] ?? activeOuter.ndcs[0];
  const activeInnerIndex = Math.max(0, activeOuter.ndcs.indexOf(activeInnerNDC));

  return (
    <div className={styles.wrap}>
      <TabView
        scrollable
        activeIndex={activeOuterIndex}
        onTabChange={e => store.selectOuterTab(tabs[e.index].type)}
        className={styles.outerTabView}
      >
        {tabs.map((tab, idx) => (
          <TabPanel
            key={tab.type}
            header={
              <NDCHeader
                heading={OUTER_TAB_LABELS[tab.type]}
                isSelected={idx === activeOuterIndex}
                tabIndex={idx}
              />
            }
          >
            <TabView
              scrollable
              activeIndex={activeInnerIndex}
              onTabChange={e => store.selectInnerNDC(tab.type, tab.ndcs[e.index])}
              className={styles.innerTabView}
            >
              {tab.ndcs.map((ndc, ndcIdx) => (
                <TabPanel
                  key={ndc}
                  header={
                    <NDCHeader
                      heading={ndc}
                      isSelected={ndcIdx === activeInnerIndex}
                      tabIndex={ndcIdx}
                    />
                  }
                >
                  <ForecastBody />
                </TabPanel>
              ))}
            </TabView>
          </TabPanel>
        ))}
      </TabView>
      <SaveBar />
    </div>
  );
});

const ForecastBody = observer(() => {
  const store = useEditStore();
  const slice = store.activeSlice;
  if (!slice) {
    return (
      <div className={styles.placeholder}>
        Select an NDC to view its forecast.
      </div>
    );
  }

  const isMAS = slice.type === 'MAS' || slice.type === 'PackoutMAS';
  const isPackout = slice.type === 'PackoutMAS' || slice.type === 'PackoutNC';

  return (
    <div className={styles.body}>
      {isPackout ? <PackoutPackRow /> : <PackSizeChips />}
      {isMAS ? (
        <>
          <MasForecastGrid />
          <SUPricesTable />
        </>
      ) : (
        <>
          {/* Variance flag threshold lives ABOVE the grid (right-anchored
              just under the calc-packs row), not inside the grid's
              header. Only NC / PackoutNC compute variance, so MAS skips
              it. */}
          <VarianceFlagPill />
          <NCForecastGrid />
        </>
      )}
    </div>
  );
});

export default ForecastTabs;
